import { useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import {
  AssistantRuntimeProvider,
  useLocalRuntime,
  ThreadPrimitive,
  ComposerPrimitive,
  MessagePrimitive,
  ActionBarPrimitive,
} from '@assistant-ui/react';
import '@assistant-ui/react-markdown/styles/dot.css';
import { MarkdownTextPrimitive } from '@assistant-ui/react-markdown';
import { Copy, RotateCcw, ArrowUp, Square } from 'lucide-react';
import { createChatAdapter } from './ChatModelAdapter';

function ChatThread() {
  return (
    <ThreadPrimitive.Root style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <ThreadPrimitive.Viewport style={{ flex: 1, overflowY: 'auto', padding: '16px 0' }}>
        <ThreadPrimitive.Empty>
          <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            justifyContent: 'center', height: '100%', gap: 8,
          }}>
            <p style={{ fontSize: 18, fontWeight: 500, color: '#94A3B8', margin: 0 }}>LLM Playground</p>
            <p style={{ fontSize: 14, color: '#475569', margin: 0 }}>Select a model and start chatting</p>
          </div>
        </ThreadPrimitive.Empty>
        <ThreadPrimitive.Messages components={{ UserMessage, AssistantMessage }} />
      </ThreadPrimitive.Viewport>
      <Composer />
    </ThreadPrimitive.Root>
  );
}

function UserMessage() {
  return (
    <MessagePrimitive.Root style={{
      display: 'flex', justifyContent: 'flex-end', padding: '6px 20px',
    }}>
      <div style={{
        maxWidth: '75%', borderRadius: 16, padding: '10px 16px',
        background: 'linear-gradient(135deg, #7C3AED, #6D28D9)',
        color: '#fff', fontSize: 14, lineHeight: 1.5,
      }}>
        <MessagePrimitive.Content components={{ Text: ({ text }) => <p style={{ margin: 0 }}>{text}</p> }} />
      </div>
    </MessagePrimitive.Root>
  );
}

function AssistantMessage() {
  return (
    <MessagePrimitive.Root style={{
      display: 'flex', padding: '6px 20px',
    }}>
      <div style={{
        maxWidth: '75%', borderRadius: 16, padding: '10px 16px',
        background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.06)',
        color: '#E2E8F0', fontSize: 14, lineHeight: 1.6,
      }}>
        <MessagePrimitive.Content components={{ Text: MarkdownTextPrimitive }} />
        <ActionBarPrimitive.Root hideWhenRunning autohide="not-last"
          style={{ display: 'flex', gap: 2, marginTop: 8 }}>
          <ActionBarPrimitive.Copy asChild>
            <button style={actionBtnStyle}><Copy size={13} /></button>
          </ActionBarPrimitive.Copy>
          <ActionBarPrimitive.Reload asChild>
            <button style={actionBtnStyle}><RotateCcw size={13} /></button>
          </ActionBarPrimitive.Reload>
        </ActionBarPrimitive.Root>
      </div>
    </MessagePrimitive.Root>
  );
}

const actionBtnStyle = {
  padding: 5, borderRadius: 6, border: 'none', cursor: 'pointer',
  background: 'transparent', color: '#64748B', transition: 'color 0.15s',
};

function Composer() {
  return (
    <ComposerPrimitive.Root style={{
      display: 'flex', alignItems: 'flex-end', gap: 10,
      padding: '12px 20px', borderTop: '1px solid rgba(255,255,255,0.08)',
      background: 'rgba(10,10,10,0.95)',
    }}>
      <ComposerPrimitive.Input
        autoFocus
        placeholder="Type a message..."
        rows={1}
        maxRows={8}
        style={{
          flex: 1, background: '#111', color: '#F1F5F9', fontSize: 14,
          borderRadius: 10, padding: '10px 16px',
          border: '1px solid rgba(255,255,255,0.1)', resize: 'none',
          outline: 'none', fontFamily: 'inherit',
        }}
      />
      <ComposerPrimitive.Send asChild>
        <button style={{
          padding: 10, borderRadius: 10, border: 'none', cursor: 'pointer',
          background: 'linear-gradient(135deg, #7C3AED, #6D28D9)', color: '#fff',
          flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <ArrowUp size={16} />
        </button>
      </ComposerPrimitive.Send>
      <ComposerPrimitive.Cancel asChild>
        <button style={{
          padding: 10, borderRadius: 10, border: 'none', cursor: 'pointer',
          background: '#991B1B', color: '#fff',
          flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Square size={16} />
        </button>
      </ComposerPrimitive.Cancel>
    </ComposerPrimitive.Root>
  );
}

export default function ChatPanel({ selectedModel, params, historyAdapter }) {
  const token = useSelector(state => state.auth.token);
  // Refs keep the adapter stable across token rotation and settings changes —
  // the adapter reads current values per request instead of being recreated
  // (recreating it mid-conversation resets the runtime).
  const tokenRef = useRef(token);
  tokenRef.current = token;
  const settingsRef = useRef({});
  settingsRef.current = {
    model: selectedModel,
    temperature: params.temperature,
    maxTokens: params.maxTokens,
    systemPrompt: params.systemPrompt,
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const adapter = useMemo(() => createChatAdapter(() => tokenRef.current, () => settingsRef.current), []);

  const runtime = useLocalRuntime(
    adapter,
    historyAdapter ? { adapters: { history: historyAdapter } } : undefined
  );

  return (
    <AssistantRuntimeProvider runtime={runtime}>
      <ChatThread />
    </AssistantRuntimeProvider>
  );
}
